import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainSidebar from '../../components/ui/MainSidebar';
import { NavigationHeader } from '../../components/ui/NavigationHeader';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { customerService, Customer } from '../../services/customerService';

const CustomerManagementDashboard = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    filterCustomers();
  }, [searchTerm, customers]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await customerService.getAll();
      setCustomers(data);
      setFilteredCustomers(data);
    } catch (err) {
      console.error('Error loading customers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const filterCustomers = () => {
    if (!searchTerm.trim()) {
      setFilteredCustomers(customers);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = customers.filter(
      (customer) =>
        customer.name?.toLowerCase().includes(term) ||
        customer.company?.toLowerCase().includes(term) ||
        customer.email?.toLowerCase().includes(term) ||
        customer.city?.toLowerCase().includes(term) ||
        customer.country?.toLowerCase().includes(term)
    );
    setFilteredCustomers(filtered);
  };

  const handleDeleteCustomer = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete customer "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await customerService.delete(id);
      await loadCustomers();
    } catch (err) {
      console.error('Error deleting customer:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete customer');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainSidebar isCollapsed={isSidebarCollapsed} onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />

      <div
        className={`flex-1 flex flex-col transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'
        }`}
      >
        <NavigationHeader />

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto p-6 space-y-6">
            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-center gap-3">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <p className="text-sm text-error font-medium">{error}</p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-text-primary">Customer Management</h1>
                <p className="text-sm text-text-secondary mt-1">
                  Manage customer information and track quote history
                </p>
              </div>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => navigate('/customer-profile-editor')}
              >
                Add Customer
              </Button>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Input
                    type="text"
                    placeholder="Search by name, company, email, location..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>
                <Button variant="outline" iconName="Filter" iconPosition="left">
                  Filters
                </Button>
                <Button variant="outline" iconName="Download" iconPosition="left">
                  Export
                </Button>
              </div>

              <div className="text-sm text-text-secondary">
                Showing {filteredCustomers.length} of {customers.length} customers
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted border-b border-border">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                        Name / Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredCustomers.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-12 text-center">
                          <Icon name="Users" size={48} className="text-text-disabled mx-auto mb-4" />
                          <p className="text-text-secondary">
                            {searchTerm ? 'No customers found matching your search' : 'No customers yet'}
                          </p>
                          {!searchTerm && (
                            <Button
                              variant="default"
                              size="sm"
                              iconName="Plus"
                              iconPosition="left"
                              onClick={() => navigate('/customer-profile-editor')}
                              className="mt-4"
                            >
                              Add First Customer
                            </Button>
                          )}
                        </td>
                      </tr>
                    ) : (
                      filteredCustomers.map((customer) => (
                        <tr key={customer.id} className="hover:bg-muted/50 transition-colors duration-150">
                          <td className="px-6 py-4">
                            <div>
                              <div className="font-medium text-text-primary">{customer.name}</div>
                              {customer.company && (
                                <div className="text-sm text-text-secondary">{customer.company}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              {customer.email && (
                                <div className="flex items-center gap-2 text-sm text-text-secondary">
                                  <Icon name="Mail" size={14} />
                                  <span>{customer.email}</span>
                                </div>
                              )}
                              {customer.phone && (
                                <div className="flex items-center gap-2 text-sm text-text-secondary">
                                  <Icon name="Phone" size={14} />
                                  <span>{customer.phone}</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-text-secondary">
                              {[customer.city, customer.country].filter(Boolean).join(', ') || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="Eye"
                                onClick={() => navigate(`/customer-profile-editor?id=${customer.id}&view=true`)}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="Edit"
                                onClick={() => navigate(`/customer-profile-editor?id=${customer.id}`)}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="Trash2"
                                onClick={() => handleDeleteCustomer(customer.id, customer.name)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CustomerManagementDashboard;